import { useSubscription } from '@/hooks/useSubscription';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MultiSelectComboBox } from '@/components/multi-select-combobox';
import type { EntitySummary, EventSummary } from '@/lib/types';
import { SceneFormValues } from '../schemas';

interface SceneMainFieldsProps {
  register: UseFormRegister<SceneFormValues>;
  control: Control<SceneFormValues>;
  errors: FieldErrors<SceneFormValues>;
  characters: EntitySummary[];
  locations: EntitySummary[];
  organizations: EntitySummary[];
  events: EventSummary[];
  selectedEvents: EventSummary[];
  selectedCharacterNames: string[];
  selectedOrganizationNames: string[];
  setSelectedEventForPopup: (event: EventSummary | null) => void;
}

export function SceneMainFields({
  register,
  control,
  errors,
  characters,
  locations,
  organizations,
  events,
  selectedEvents,
  selectedCharacterNames,
  selectedOrganizationNames,
  setSelectedEventForPopup,
}: SceneMainFieldsProps) {
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isGenerativeAiAllowed = hasFeature('is_generative_ai_allowed');

  const entityOptions = (items: { id: string; name: string }[]) =>
    items.map((e) => ({ value: e.id, label: e.name }));

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 8 }}>
        <TextField
          {...register('name')}
          label={t('entity_form.scenes.name_label')}
          placeholder={t('entity_form.scenes.name_placeholder')}
          fullWidth
          error={!!errors.name}
          helperText={errors.name?.message}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Controller
          name="status.phase"
          control={control}
          render={({ field }) => (
            <TextField
              select
              label={t('entity_form.scenes.status_label')}
              fullWidth
              {...field}
              value={field.value || 'idle'}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="idle">{t('writers_room.phases.idle')}</option>
              {isGenerativeAiAllowed && <option value="snapshot">{t('writers_room.phases.snapshot')}</option>}
              {isGenerativeAiAllowed && <option value="prepared">{t('writers_room.phases.prepared')}</option>}
              <option value="writing">{t('writers_room.phases.writing')}</option>
              <option value="editing">{t('writers_room.phases.editing')}</option>
              {isGenerativeAiAllowed && <option value="validating">{t('writers_room.phases.validating')}</option>}
              <option value="approved">{t('writers_room.phases.approved')}</option>
              <option value="needs_fixes">{t('writers_room.phases.needs_fixes')}</option>
              {isGenerativeAiAllowed && <option value="auto_fixing">{t('writers_room.phases.auto_fixing')}</option>}
              <option value="ready">{t('writers_room.phases.ready')}</option>
            </TextField>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Box>
          <TextField
            {...register('description')}
            label={t('entity_form.scenes.description_label')}
            placeholder={t('entity_form.scenes.description_placeholder')}
            fullWidth
            multiline
            minRows={6}
            error={!!errors.description}
            helperText={errors.description?.message}
            InputProps={{
              sx: {
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            {selectedEvents.map(event => (
              <Chip
                key={event.id}
                label={event.name}
                onClick={() => setSelectedEventForPopup(event)}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Controller
          name="eventIds"
          control={control}
          render={({ field }) => (
            <MultiSelectComboBox
              options={entityOptions(events)}
              selected={field.value || []}
              onChange={field.onChange}
              placeholder={t('entity_form.scenes.select_events_placeholder')}
              searchPlaceholder={t('entity_form.scenes.search_events')}
              emptyPlaceholder={t('entity_form.scenes.no_events_found')}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Controller
          name="povCharacterId"
          control={control}
          render={({ field }) => (
            <MultiSelectComboBox
              options={entityOptions(characters)}
              selected={field.value ? [field.value] : []}
              onChange={(ids) => {
                const prevValue = field.value;
                const newValue = ids.find(id => id !== prevValue) || ids[0] || '';
                field.onChange(newValue);
              }}
              placeholder={t('entity_form.scenes.select_pov_placeholder')}
              searchPlaceholder={t('entity_form.scenes.search_characters')}
              emptyPlaceholder={t('entity_form.scenes.no_characters_found')}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Box>
          <TextField
            {...register('minorCharacters')}
            label={t('entity_form.scenes.minor_characters_label')}
            placeholder={t('entity_form.scenes.minor_characters_placeholder')}
            fullWidth
            multiline
            minRows={3}
            InputProps={{
              sx: {
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            {selectedCharacterNames.map((name: string) => (
              <Chip key={name} label={name} size="small" variant="outlined" />
            ))}
            {selectedOrganizationNames.map((name: string) => (
              <Chip key={name} label={name} size="small" variant="outlined" color="info" />
            ))}
          </Stack>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Controller
          name="characterIds"
          control={control}
          render={({ field }) => (
            <MultiSelectComboBox
              options={entityOptions(characters)}
              selected={field.value || []}
              onChange={field.onChange}
              placeholder={t('entity_form.scenes.select_characters_placeholder')}
              searchPlaceholder={t('entity_form.scenes.search_characters')}
              emptyPlaceholder={t('entity_form.scenes.no_characters_found')}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Controller
          name="locationIds"
          control={control}
          render={({ field }) => (
            <MultiSelectComboBox
              options={entityOptions(locations)}
              selected={field.value || []}
              onChange={field.onChange}
              placeholder={t('entity_form.scenes.select_locations_placeholder')}
              searchPlaceholder={t('entity_form.scenes.search_locations')}
              emptyPlaceholder={t('entity_form.scenes.no_locations_found')}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Controller
          name="organizationIds"
          control={control}
          render={({ field }) => (
            <MultiSelectComboBox
              options={entityOptions(organizations)}
              selected={field.value || []}
              onChange={field.onChange}
              placeholder={t('entity_form.scenes.select_organizations_placeholder')}
              searchPlaceholder={t('entity_form.scenes.search_organizations')}
              emptyPlaceholder={t('entity_form.scenes.no_organizations_found')}
            />
          )}
        />
      </Grid>
    </Grid>
  );
}
